import React, { useState } from 'react'
import styles from './FromFile.module.scss'
import { UploadCloud as UploadIcon } from 'almanac-ui/icons/16/UploadCloud'
import { ReactComponent as FileIcon } from 'assets/icons/doc.svg'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { SourceCard } from 'components/SourceCard/SourceCard'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import cx from 'classnames'
import { CharacterLimitNote } from './CharacterLimitNote'
import { reportError } from 'utils/error'
import { validationSchema } from '../validation/validationSchema'
import { Upload5 } from 'blaze-ui/icons/20'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { Text } from 'blaze-ui/Text/Text'

export type FromFileProps = {
  initialFileName?: string | null
  initialFileUrl?: string | null
  onChange: (sampleText: string | null, file: File | null) => void
  showError: boolean
}

const validFileExtensions = ['md', 'txt', 'pdf']

const processFile = async (file: File): Promise<string> => {
  const fileExtension = /\.(?<ext>\w+)$/g.exec(file.name)?.groups?.ext ?? ''
  switch (fileExtension) {
    case 'md':
    case 'txt':
      return await file.text()
    case 'pdf': {
      const formData = new FormData()
      formData.append('pdf_file', file)
      const response = await sendToApi<{ text: string }>('/extract-pdf-text', {
        method: 'POST',
        body: formData,
      })
      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        if (response.type === 'error') {
          throw new Error(`Failed to extract PDF text: ${response.statusText}`)
        }
        throw new Error(`Failed to extract PDF text: ${response.data.message}`)
      }
      return response.data.text
    }
    default:
      throw new Error(`Unsupported file extension: ${fileExtension}`)
  }
}

export const FromFile: React.FC<FromFileProps> = ({ initialFileName, initialFileUrl, onChange, showError }) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const [fileName, setFileName] = useState(initialFileName || '')
  const [uploaded, setUploaded] = useState(false || !!initialFileName)
  const [fileError, setFileError] = useState<string | undefined>(undefined)

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    handleFiles(e.dataTransfer.files)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }

  const handleFiles = (files: FileList) => {
    setFileError(undefined)
    setUploaded(false)
    if (!files || files.length === 0) return

    try {
      validationSchema.validateSync(files[0]) && uploadFile(files[0])
    } catch (e) {
      if (e instanceof Error && e.name === 'ValidationError') {
        setFileError(e.message)
      }
    }
  }

  const handleFilePickerChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.files && handleFiles(e.target.files)
  }

  const uploadFile = async (file: File) => {
    try {
      setFileName(file.name)
      const fileContents = await processFile(file)
      setUploaded(true)
      onChange(fileContents, file)
    } catch (e) {
      reportError(e)
      setFileName('')
      setUploaded(false)
    }
  }

  return (
    <div className={styles.FromFile}>
      {!fileName && (
        <div className={styles.dropZoneContainer}>
          <div
            className={cx(styles.dropZone, { [styles.hasError]: (showError && !fileName) || fileError })}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            data-testid="drop-zone"
          >
            <div className={styles.instruction}>
              <div className={styles.instructionText}>
                {isSystemRedesignEnabled && <Upload5 size={20} />}Drag & drop file
              </div>
              {isSystemRedesignEnabled && (
                <Text variant="label" color="var(--dark-30)">
                  Supported files: Markdown, TXT, PDF
                </Text>
              )}
            </div>
            {!isSystemRedesignEnabled && (
              <div className={styles.fileTypePills}>
                <div className={styles.pill}>Markdown</div>
                <div className={styles.pill}>TXT</div>
                <div className={styles.pill}>PDF</div>
              </div>
            )}
            <div className={styles.filePickerBtn}>
              <input
                type="file"
                className={styles.filePickerInput}
                accept={validFileExtensions.map((x) => `.${x}`).join(',')}
                onChange={handleFilePickerChange}
              />
              {!isSystemRedesignEnabled && <UploadIcon />}
              Upload from Your Device
            </div>
          </div>
          {showError && !fileName && !fileError && <p className={styles.error}>Required</p>}
          {fileError && <p className={styles.error}>{fileError}</p>}
        </div>
      )}
      {fileName && (
        <SourceCard
          icon={<FileIcon className={styles.fileIcon} />}
          title={
            <ConditionalWrapper
              condition={!!initialFileUrl}
              wrapper={(children) => (
                <a href={initialFileUrl as string} target="_blank" rel="noreferrer">
                  {children}
                </a>
              )}
            >
              {fileName}
              {uploaded ? '' : '(Uploading...)'}
            </ConditionalWrapper>
          }
          onDismiss={() => {
            setFileName('')
            setUploaded(false)
            onChange(null, null)
          }}
        />
      )}
      <CharacterLimitNote />
    </div>
  )
}
